import {
    getMySessions,
    getAllSessions,
    revokeMySession,
    revokeAllMySessions,
    adminRevokeSession,
    adminRevokeUserSessions,
} from "../../api/session";

const session = {
    namespaced: true,
    state: {
        mySessions: [],
        allSessions: [],
        loading: false,
        error: null,
        pagination: {
            page: 1,
            perPage: 50,
            total: 0,
            totalPages: 0,
        },
    },
    getters: {
        getMySessions: (state) => state.mySessions,
        getAllSessions: (state) => state.allSessions,
        isLoading: (state) => state.loading,
        getError: (state) => state.error,
        getPagination: (state) => state.pagination,
        getCurrentSession: (state) => state.mySessions.find((s) => s.isCurrent),
    },
    mutations: {
        SET_MY_SESSIONS(state, sessions) {
            state.mySessions = sessions;
        },
        SET_ALL_SESSIONS(state, sessions) {
            state.allSessions = sessions;
        },
        SET_LOADING(state, loading) {
            state.loading = loading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_PAGINATION(state, pagination) {
            state.pagination = {
                page: pagination.page || 1,
                perPage: pagination.perPage || 50,
                total: pagination.total || 0,
                totalPages: pagination.totalPages || 0,
            };
        },
        REMOVE_SESSION(state, sessionId) {
            state.mySessions = state.mySessions.filter((s) => s._id !== sessionId);
            state.allSessions = state.allSessions.filter((s) => s._id !== sessionId);
        },
        CLEAR_SESSIONS(state) {
            state.mySessions = [];
            state.allSessions = [];
        },
    },
    actions: {
        async fetchMySessions({ commit }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                const response = await getMySessions();
                commit("SET_MY_SESSIONS", response.data.sessions || []);
                return response.data;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to fetch sessions");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async fetchAllSessions({ commit }, params = {}) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                const response = await getAllSessions(params);
                commit("SET_ALL_SESSIONS", response.data.sessions || []);
                commit("SET_PAGINATION", {
                    page: response.data.page,
                    perPage: response.data.perPage,
                    total: response.data.total,
                    totalPages: response.data.totalPages,
                });
                return response.data;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to fetch all sessions");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async revokeSession({ commit, dispatch }, sessionId) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                await revokeMySession(sessionId);
                commit("REMOVE_SESSION", sessionId);
                return true;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to revoke session");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async revokeAllOtherSessions({ commit, dispatch }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                const response = await revokeAllMySessions();
                await dispatch("fetchMySessions");
                return response.data;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to revoke all sessions");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async adminRevokeSession({ commit, dispatch }, { sessionId, reason }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                await adminRevokeSession(sessionId, reason);
                commit("REMOVE_SESSION", sessionId);
                return true;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to revoke session");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        async adminRevokeUserSessions({ commit, dispatch }, { userId, reason }) {
            commit("SET_LOADING", true);
            commit("SET_ERROR", null);
            try {
                const response = await adminRevokeUserSessions(userId, reason);
                await dispatch("fetchAllSessions");
                return response.data;
            } catch (error) {
                commit("SET_ERROR", error.message || "Failed to revoke user sessions");
                throw error;
            } finally {
                commit("SET_LOADING", false);
            }
        },

        clearSessions({ commit }) {
            commit("CLEAR_SESSIONS");
        },
    },
};

export default session;
